/**
 * Generic field renderer for onboarding wizard steps. Centralises
 * input rendering so each step component stays focused on its
 * domain-specific layout (e.g. industry picker, KPI grid) and
 * delegates plain text/number/url fields here.
 */
import React from 'react';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';

export function TextField({ field, value, onChange, testIdPrefix = 'onb-field' }) {
  const id = `${testIdPrefix}-${field.key}`;
  const inputType = field.type === 'email'
    ? 'email'
    : field.type === 'url'
      ? 'url'
      : field.type === 'currency' || field.type === 'number'
        ? 'number'
        : field.type === 'date'
          ? 'date'
          : 'text';
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium text-slate-700">
        {field.label} {field.required && <span className="text-rose-500">*</span>}
      </Label>
      <Input
        id={id}
        type={inputType}
        value={value ?? ''}
        step={field.step}
        min={field.min}
        max={field.max}
        onChange={(e) => {
          const v = inputType === 'number'
            ? (e.target.value === '' ? null : Number(e.target.value))
            : e.target.value;
          onChange(field.key, v);
        }}
        data-testid={id}
      />
      {field.hint && (
        <p className="text-[10px] text-slate-500">{field.hint}</p>
      )}
    </div>
  );
}

export function SelectField({ field, value, onChange, testIdPrefix = 'onb-field' }) {
  const id = `${testIdPrefix}-${field.key}`;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium text-slate-700">
        {field.label} {field.required && <span className="text-rose-500">*</span>}
      </Label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(field.key, e.target.value || null)}
        className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
        data-testid={id}
      >
        <option value="">— Pick one —</option>
        {(field.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
